import { Drill } from '@/components/drill-library/types/drill-types';
import { generateCSV, escapeCSV } from './csv-utils';
import { getTagValues } from './drill-taxonomy';

type Taxonomy = { [key: string]: { type: string; options: string[] } };

/**
 * Categories that support multiple values and should be exported as checkbox-style columns
 * Even though they may be defined as 'select' type in the taxonomy
 */
const MULTI_VALUE_SELECT_CATEGORIES = ['Phase', 'Posture'];

/**
 * Transform taxonomy value to display value for CSV export
 * Uses current taxonomy to map stored values to display values
 * Handles renamed options by matching stored values to current taxonomy options
 */
function transformValueForExport(
  categoryName: string, 
  value: string, 
  taxonomy: Taxonomy
): string {
  const category = taxonomy[categoryName];
  if (!category) {
    // Category doesn't exist in taxonomy, return as-is
    return value;
  }

  // Check if value exactly matches a current taxonomy option
  if (category.options.includes(value)) {
    return value;
  }

  // Try case-insensitive matching
  const caseInsensitiveMatch = category.options.find(
    opt => opt.toLowerCase() === value.toLowerCase()
  );
  if (caseInsensitiveMatch) {
    return caseInsensitiveMatch;
  }

  // Try partial matching (for renamed options like "vertical-shin" -> "Vertical")
  // Check if any taxonomy option contains the value or vice versa
  const partialMatch = category.options.find(opt => {
    const optLower = opt.toLowerCase().replace(/[^a-z0-9]/g, '');
    const valueLower = value.toLowerCase().replace(/[^a-z0-9]/g, '');
    return optLower.includes(valueLower) || valueLower.includes(optLower);
  });
  if (partialMatch) {
    return partialMatch;
  }

  // No match found - return stored value as-is (for backward compatibility)
  return value;
}
import logger from '@/lib/logging';

/**
 * Transform display value back to taxonomy value for CSV import
 * Uses current taxonomy to map display values to stored values
 */
export function transformValueForImport(
  categoryName: string, 
  displayValue: string,
  taxonomy: Taxonomy
): string {
  const category = taxonomy[categoryName];
  if (!category) {
    // Category doesn't exist in taxonomy, return as-is
    return displayValue;
  }

  const normalized = displayValue.trim();
  
  // Check if display value exactly matches a current taxonomy option
  if (category.options.includes(normalized)) {
    return normalized;
  }

  // Try case-insensitive matching
  const caseInsensitiveMatch = category.options.find(
    opt => opt.toLowerCase() === normalized.toLowerCase()
  );
  if (caseInsensitiveMatch) {
    return caseInsensitiveMatch;
  }

  // Try partial matching (for backward compatibility with old exports)
  const partialMatch = category.options.find(opt => {
    const optLower = opt.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedLower = normalized.toLowerCase().replace(/[^a-z0-9]/g, '');
    return optLower.includes(normalizedLower) || normalizedLower.includes(optLower);
  });
  if (partialMatch) {
    return partialMatch;
  }

  // No match found - return display value as-is
  return displayValue;
}

/**
 * Export drills to CSV format
 * Columns: Title, URL, Description, and all taxonomy categories
 * For checkbox categories and multi-value select categories, creates one column per option
 */
export function exportDrillsToCSV(drills: Drill[], taxonomy: Taxonomy): void {
  try {
    // Build column headers
    const headers: string[] = ['YouTube ID', 'Title', 'URL', 'Description', 'Created By', 'Created At', 'Changed By', 'Changed At'];
    
    // Add taxonomy category columns
    const categoryOrder = Object.keys(taxonomy).sort();
    
    for (const categoryName of categoryOrder) {
      const category = taxonomy[categoryName];
      
      // CRITICAL: Multi-value select categories (Phase, Pattern) get checkbox-style columns
      // This allows proper representation of drills in multiple phases/patterns
      if (category.type === 'select' && MULTI_VALUE_SELECT_CATEGORIES.includes(categoryName)) {
        // Create one column per option, like checkbox categories
        for (const option of category.options) {
          const columnName = `${categoryName} - ${option}`;
          headers.push(columnName);
        }
      } else if (category.type === 'select') {
        // Regular select - single column
        headers.push(categoryName);
      } else if (category.type === 'checkbox') {
        // Checkbox - one column per option
        for (const option of category.options) {
          const columnName = `${categoryName} - ${option}`;
          headers.push(columnName);
        }
      }
    }
    
    // Build rows
    const rows: string[][] = [headers];
    
    for (const drill of drills) {
      const row: string[] = [];
      
      // Fixed columns
      row.push(extractYouTubeId(drill.videoUrl)); // YouTube ID
      row.push(drill.name || '');
      row.push(drill.videoUrl || '');
      row.push(drill.description || '');
      
      // Audit columns
      row.push(drill.createdBy || '');
      row.push(drill.createdAt || '');
      row.push(drill.changedBy || '');
      row.push(drill.changedAt || '');
      
      // Taxonomy columns
      for (const categoryName of categoryOrder) {
        const category = taxonomy[categoryName];
        const drillValues = getTagValues(drill, categoryName);
        
        // CRITICAL: Multi-value select categories get checkbox-style columns
        if (category.type === 'select' && MULTI_VALUE_SELECT_CATEGORIES.includes(categoryName)) {
          for (const option of category.options) {
            const isSelected = drillValues.includes(option);
            row.push(isSelected ? 'X' : '');
          }
        } else if (category.type === 'select') {
          // Regular select - single value
          if (drillValues.length > 0) {
            const value = drillValues[0];
            const displayValue = transformValueForExport(categoryName, value, taxonomy);
            row.push(displayValue || '');
          } else {
            row.push('');
          }
        } else if (category.type === 'checkbox') {
          // One column per option - mark with "X" if selected
          for (const option of category.options) {
            row.push(drillValues.includes(option) ? 'X' : '');
          }
        }
      }
      
      rows.push(row);
    }
    
    // Generate CSV content
    const csvContent = generateCSV(rows);
    
    // Create and download file
    const dataBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `drill-library-export-${new Date().toISOString().split('T')[0]}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    window.URL.revokeObjectURL(url);
  } catch (error) {
    logger.error('Error exporting drills to CSV:', error);
    throw new Error('Failed to export drills to CSV');
  }
}

/**
 * Extract YouTube video ID from URL
 */
function extractYouTubeId(url: string | undefined): string {
  if (!url || typeof url !== 'string') return '';
  
  try {
    const urlObj = new URL(url.trim());
    
    // Handle youtu.be short URLs
    if (urlObj.hostname === 'youtu.be') {
      const id = urlObj.pathname.slice(1);
      return id || '';
    }
    
    // Handle youtube.com URLs
    if (urlObj.hostname.includes('youtube.com')) {
      const id = urlObj.searchParams.get('v');
      return id || '';
    }
    
    // Handle /embed/{id} or /v/{id} URLs
    const parts = urlObj.pathname.split('/').filter(Boolean);
    const maybeId = parts[parts.length - 1];
    return maybeId || '';
  } catch {
    return '';
  }
}

